import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../../redux/slices/appInfo';
import { Button } from 'antd';
import { ICGoogle } from '../../../icons/index';
import { useKeycloakAuth } from '../../../auth/useKeycloakAuth';

const Wrapper = styled.div`
    background-color: #7357ff;
    background-image: url('/images/bg-pattern.png');
    background-size: cover;
    background-repeat: no-repeat;
`;

/**
 * Google-styled button (mirrors pages/auth/login/index.tsx). Kept in sync
 * there — both pages share the same visual because they trigger the same
 * Keycloak flow. If you change one, change the other.
 */
const GoogleButton = styled(Button)`
    &&.ant-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        height: 48px;
        padding: 0 16px;
        background-color: #ffffff;
        border: 1px solid #dadce0;
        border-radius: 8px;
        color: #3c4043;
        font-family:
            'Google Sans',
            'Roboto',
            system-ui,
            -apple-system,
            'Segoe UI',
            sans-serif;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0.0107em;
        box-shadow: none;
        transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease;
    }
    &&.ant-btn:hover,
    &&.ant-btn:focus {
        background-color: #f8f9fa;
        border-color: #dadce0;
        color: #3c4043;
        box-shadow: 0 1px 2px rgba(60, 64, 67, 0.1);
    }
    &&.ant-btn:active {
        background-color: #f1f3f4;
        border-color: #dadce0;
        color: #3c4043;
        box-shadow: 0 1px 2px rgba(60, 64, 67, 0.15);
    }
    &&.ant-btn > .anticon + span {
        margin-left: 0;
    }
`;

/**
 * Sign-up landing page. The actual account creation happens on
 * Keycloak's register page (redirect via `keycloak.register()`).
 * On a successful return the user lands on /auth/callback (see
 * pages/auth/callback/index.tsx), which hydrates Redux and POSTs
 * /api/users/sync before navigating to /work-order.
 *
 * Mirrors pages/auth/login/index.tsx: same wrapper, same auto-skip
 * when already authenticated, same Google-styled "Continue with Google" CTA.
 */
export const RegisterPage: React.FC = () => {
    const { isAuthenticated, register } = useKeycloakAuth();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        // Reset the global loading overlay on (re-)mount. See the matching
        // comment in pages/auth/login/index.tsx for the full reasoning:
        // handleRegister leaves `isShowLoading=true` during the Keycloak
        // redirect, so we need to clear it when the user lands back here
        // (cancel, IdP error, etc.).
        dispatch(setLoading(false));
    }, [dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/work-order', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const handleRegister = () => {
        dispatch(setLoading(true));
        // 'google' => kc_idp_hint=google => Keycloak forwards straight to
        // the Google IdP broker for account creation, skipping the realm
        // register page.
        register('google');
        // The redirect leaves the page, so we don't unset the loading
        // flag. If redirect is blocked for any reason the spinner will
        // stay until the next mount, which is acceptable. The mount
        // effect above clears it on re-entry.
    };

    return (
        <Wrapper className="flex w-screen h-screen items-center justify-center bg-surface-low">
            <div className="shadow-e-03 max-md:max-w-[478px] max-md:w-auto w-[478px] p-[32px] flex flex-col gap-[24px] rounded-[12px] overflow-hidden box-border bg-white">
                <div className="flex justify-center">
                    <h1 className="text-heading-5 text-high-em font-semibold">
                        Create your Auto Track account
                    </h1>
                </div>
                <p className="text-body text-mid-em text-center">
                    You'll be redirected to continue with Google to finish
                    creating your account.
                </p>
                <div className="flex flex-col w-full gap-[16px] pt-[8px]">
                    <GoogleButton block size="large" onClick={handleRegister}>
                        <ICGoogle width={18} height={18} />
                        <span>Continue with Google</span>
                    </GoogleButton>
                </div>
                <div className="flex gap-[8px] items-center justify-center text-center text-high-em text-body pt-[8px]">
                    <span>Already have an account?</span>
                    <Link
                        to="/login"
                        className="underline text-info-500 font-medium"
                    >
                        Sign in
                    </Link>
                </div>
            </div>
        </Wrapper>
    );
};
